import React, { useState } from 'react'
import {
  Button,
  Card,
  CheckboxInput,
  CheckCircleIcon,
  Close,
  EyeIcon,
  Flex,
  H2,
  H4,
  Input,
  InputModal,
  Line,
  P,
  PendingIcon,
  ReportIcon,
  SelectDropdown,
  Span,
  StatusLabel,
  ToolTip,
} from '../../lib/components'
import { useBreakpoint } from '../../lib/utils/hooks'
import { accountStatusMap, KycStatus, WithdrawalDetailPresenterProps } from '../../lib/types'
import { XL } from '../../lib/utils/constants'
import {
  displayDollar,
  formatCentsAsDecimalString,
  formatPercentage,
  formatStatus,
  getAllowedWithdrawalStatusOptions,
  getCentsField,
  getOrderStatusErrorMessage,
  isPositiveDecimalAmount,
} from '../../lib/utils/helpers'
import {
  WithdrawalStatus,
  SOURCE_OF_FUNDS_LABELS,
  SOURCE_OF_INCOME_LABELS,
} from '../../lib/utils/message'
import { InfoIcon } from '../../lib/components/Icon'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
const WithdrawalDetailPresenter = ({
  opportunity,
  investorProfile,
  kycData,
  investorBankDetails,
  withdrawalRequestDetail,
  allowWithdrawalOrderStatusUpdates = true,
  handleGenerateKYCReportBtnClick,
  handleStatus,
}: WithdrawalDetailPresenterProps) => {
  const { breakpoint } = useBreakpoint()
  const [displayWithdrawalStatusModal, setDisplayWithdrawalStatusModal] = useState(false)
  const [withdrawalStatusValue, setWithdrawalStatusValue] = useState(withdrawalRequestDetail.status)
  const [error, setError] = useState<string>('')
  const [units, setUnits] = useState<string>('')
  const [isManualUnitsInputEnabled, setIsManualUnitsInputEnabled] = useState(false)
  const [description, setDescription] = useState<string>('')
  const [actualAmountSentStr, setActualAmountSentStr] = useState<string>('')
  const [confirmationModal, setConfirmationModal] = useState(false)

  const getDefaultActualAmountSentStr = () => {
    const received = getCentsField(
      withdrawalRequestDetail,
      'actualAmountReceived',
      'actual_amount_received',
    )
    if (received !== undefined) return formatCentsAsDecimalString(received)
    return formatCentsAsDecimalString(withdrawalRequestDetail?.amount)
  }

  return (
    <Flex variant='column' classes={['investment-detail-wrapper']}>
      <H2
        text='Redemption Detail'
        classes={['align-center', 'investment-detail-wrapper-heading']}
      />

      {investorProfile && investorProfile?.accountStatus === accountStatusMap.DORMANT && (
        <div
          className='warning-container-fit-content  mb-24'
          data-testid='withdrawal-detail-dormant-user-disclaimer'
        >
          <PendingIcon classes={['']} width='20px' height='20px' />
          This user is dormant
        </div>
      )}

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        {opportunity && opportunity.opportunity_title && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Opportunity Details' classes={['mb-16']} />
            <Flex variant='space-between'>
              <Span variant='bold' text='Opportunity Name' />
              <Span variant='thin' text={(opportunity && opportunity.opportunity_title) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Asset class' />
              <Span variant='thin' text={(opportunity && opportunity.asset_class) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Minimum Amount' />
              <Span
                variant='thin'
                text={displayDollar(
                  String((opportunity && opportunity.ticket_size) ?? 0),
                  opportunity?.currency_code,
                )}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='IRR' />
              <Span
                variant='thin'
                text={formatPercentage((opportunity && opportunity.investment_return) ?? 0)}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Raise Goal' />
              <Span
                variant='thin'
                text={
                  opportunity && opportunity.raise_goal !== undefined
                    ? displayDollar(String(opportunity.raise_goal), opportunity.currency_code)
                    : 'N/A'
                }
              />
            </Flex>
          </Card>
        )}

        {investorProfile && investorProfile.email && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Client Details' classes={['mb-16']} />
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Name' />
              <Span variant='thin' text={(investorProfile && investorProfile.name) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Email' />
              <Span variant='thin' text={(investorProfile && investorProfile.email) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Phone Number' />
              <Span
                variant='thin'
                text={String(investorProfile && investorProfile.phoneNumber) ?? ''}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Country of Residence' />
              <Span
                variant='thin'
                text={(investorProfile && investorProfile.countryOfResidence) ?? ''}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Client ID' />
              <Span
                variant='thin'
                text={(investorProfile && investorProfile?.investorReference) ?? ''}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Portfolio ID' />
              <Span variant='thin' text={(kycData && kycData.investor_portfolio_id) ?? ''} />
            </Flex>
            {kycData && kycData?.metadata?.welcomeLetter && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='withdrawal-detail-welcome-letter'
              >
                <Span variant='bold' text='Welcome Letter' />
                <EyeIcon
                  classes={['align-center', 'pointer', 'mt-4']}
                  height='24px'
                  width='24px'
                  onClick={async () => {
                    if (kycData && kycData?.metadata?.welcomeLetter) {
                      logCrmEvent(EngagementEvents.CLICKED_VIEW_WELCOME_LETTER, {
                        page: 'withdrawal-detail',
                        withdrawalId: withdrawalRequestDetail?.id,
                        investorId: investorProfile?.userId,
                      })
                      const url = kycData && kycData?.metadata?.welcomeLetter
                      url && window.open(url, '_blank')
                    }
                  }}
                  dataTestId='withdrawal-detail-welcome-letter-view-icon'
                />
              </Flex>
            )}
          </Card>
        )}
      </Flex>

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        {kycData && kycData.status && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='KYC Details' classes={['mb-16']} />
            <Flex variant='space-between'>
              <Span variant='bold' text='Status' />
              <Span variant='thin' text={(kycData && kycData.status) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Client Type' />
              <Span variant='thin' text={(investorProfile && investorProfile.role) ?? ''} />
            </Flex>
            {kycData && kycData?.id && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='withdrawal-detail-kyc-report'
              >
                <Span variant='bold' text='KYC Report' />
                <EyeIcon
                  classes={['align-center', 'pointer', 'mt-4']}
                  height='24px'
                  width='24px'
                  onClick={() => handleGenerateKYCReportBtnClick(String(kycData?.id))}
                  dataTestId='withdrawal-detail-kyc-report-view-icon'
                />
              </Flex>
            )}
          </Card>
        )}
        {investorBankDetails && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Client Bank Details' classes={['mb-16']} />
            <Flex
              variant='space-between'
              dataTestId='withdrawal-detail-investor-bank-details-beneficiary-name'
            >
              <Span variant='bold' text='Beneficiary Name' />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.beneficiaryName) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-beneficiary-account-number'
            >
              <Span variant='bold' text='Beneficiary Account Number' />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.accountNumber) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-beneficiary-bank-name'
            >
              <Span variant='bold' text={"Beneficiary's Bank Name"} />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.beneficiaryBank) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-beneficiary-bank-address'
            >
              <Span variant='bold' text={"Beneficiary's Bank Address"} />

              {investorBankDetails && investorBankDetails?.bankAddress?.length > 25 ? (
                <ToolTip
                  content={investorBankDetails && investorBankDetails?.bankAddress}
                  contentClassNames={['tool-tip-container-fit']}
                >
                  <span>
                    <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                  </span>
                </ToolTip>
              ) : (
                <Span
                  variant='thin'
                  classes={['right']}
                  text={(investorBankDetails && investorBankDetails.bankAddress) ?? ''}
                />
              )}
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-swift-code'
            >
              <Span variant='bold' text={'SWIFT Code'} />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.swiftCode) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-cif'
            >
              <Span variant='bold' text={'CIF Number'} />
              <Span variant='thin' text={(investorBankDetails && investorBankDetails.cif) ?? ''} />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-source-of-funds'
            >
              <Span variant='bold' text={'Source of Funds'} />

              {(() => {
                const sourceOfFunds = investorBankDetails?.sourceOfFunds ?? []
                const sourceOfFundsText = sourceOfFunds
                  .map((code) => SOURCE_OF_FUNDS_LABELS[code] ?? code)
                  .join(', ')

                return sourceOfFundsText.length > 25 ? (
                  <ToolTip
                    content={sourceOfFundsText}
                    contentClassNames={['tool-tip-container-fit']}
                  >
                    <span>
                      <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                    </span>
                  </ToolTip>
                ) : (
                  <Span variant='thin' classes={['right']} text={sourceOfFundsText} />
                )
              })()}
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-investor-bank-details-source-of-income'
            >
              <Span variant='bold' text={'Source of Income'} />

              {(() => {
                const sourceOfIncome = investorBankDetails?.sourceOfIncome ?? []
                const sourceOfIncomeText = sourceOfIncome
                  .map((code) => SOURCE_OF_INCOME_LABELS[code] ?? code)
                  .join(', ')

                return sourceOfIncomeText.length > 25 ? (
                  <ToolTip
                    content={sourceOfIncomeText}
                    contentClassNames={['tool-tip-container-fit']}
                  >
                    <span>
                      <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                    </span>
                  </ToolTip>
                ) : (
                  <Span variant='thin' classes={['right']} text={sourceOfIncomeText} />
                )
              })()}
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Bank Details Confirmed' />

              {withdrawalRequestDetail && withdrawalRequestDetail.metadata?.bankDetailsConfirmed ? (
                <CheckCircleIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='withdrawal-detail-bank-details-confirmed-icon'
                />
              ) : (
                <ToolTip content={'Not Confirmed'} contentClassNames={['tool-tip-container-fit']}>
                  <span>
                    <ReportIcon
                      classes={['mr-4']}
                      height='23px'
                      dataTestId='withdrawal-detail-bank-details-not-confirmed-icon'
                    />
                  </span>
                </ToolTip>
              )}
            </Flex>
          </Card>
        )}
      </Flex>

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        {kycData && kycData.status && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Overview of the status and meaning' classes={['mb-16']} />
            <div className='mt-12'>
              <Span variant='bold' text='Initiated:' />
              <Span
                variant='thin'
                text={
                  'Set by the system automatically – When the client has started the withdrawal process.'
                }
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Order placed:' />
              <Span
                variant='thin'
                text={`Set by the system automatically – When all the steps from the client's side are completed, and the order is successfully placed.`}
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Rejected:' />
              <Span
                variant='thin'
                text={`To be set by the admin manually – When the admin reviews and rejects the redemption request.`}
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Trade executed:' />
              <Span
                variant='thin'
                text={`To be set by the admin manually – When the trade has been executed, and the admin needs to input the units sold sold from the client's allocation.`}
              />
            </div>
          </Card>
        )}

        {withdrawalRequestDetail && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Redemption Details' classes={['mb-16']} />
            <Flex variant='space-between' dataTestId='withdrawal-detail-order-id'>
              <Span variant='bold' text='Order ID' />
              <Span
                variant='thin'
                text={(withdrawalRequestDetail && withdrawalRequestDetail.order_id) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-withdrawal-status'
            >
              <Span variant='bold' text='Redemption Status' />
              <Span
                variant='thin'
                text={formatStatus(
                  (withdrawalRequestDetail && withdrawalRequestDetail.status) ?? '',
                )}
              />
            </Flex>

            {/* Showing withdrawal amount */}
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-withdrawal-amount'
            >
              <Span variant='bold' text='Requested amount' />
              <Span
                variant='thin'
                text={displayDollar(
                  String((withdrawalRequestDetail && withdrawalRequestDetail.amount) ?? 0),
                  opportunity.currency_code ?? 'USD',
                  2,
                  true,
                )}
              />
            </Flex>
            {(() => {
              const actualAmountReceived = getCentsField(
                withdrawalRequestDetail,
                'actualAmountReceived',
                'actual_amount_received',
              )
              if (actualAmountReceived === undefined) return null
              return (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='withdrawal-detail-actual-amount-received'
                >
                  <Span variant='bold' text='Actual amount received' />
                  <Span
                    variant='thin'
                    text={displayDollar(
                      String(actualAmountReceived),
                      opportunity.currency_code ?? 'USD',
                      2,
                      true,
                    )}
                  />
                </Flex>
              )
            })()}
            {(() => {
              const actualAmountSent = getCentsField(
                withdrawalRequestDetail,
                'actualAmountSent',
                'actual_amount_sent',
              )
              if (actualAmountSent === undefined) return null
              return (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='withdrawal-detail-actual-amount-sent'
                >
                  <Span variant='bold' text='Actual amount sent' />
                  <Span
                    variant='thin'
                    text={displayDollar(
                      String(actualAmountSent),
                      opportunity.currency_code ?? 'USD',
                      2,
                      true,
                    )}
                  />
                </Flex>
              )
            })()}
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-withdrawal-platform-fee'
            >
              <Span variant='bold' text='Platform Fee' />
              <Span
                variant='thin'
                text={displayDollar(
                  String(
                    (withdrawalRequestDetail && withdrawalRequestDetail?.platform_fee?.amount) ?? 0,
                  ),
                  (withdrawalRequestDetail &&
                    withdrawalRequestDetail?.platform_fee?.currency_code) ??
                    'USD',
                  2,
                  true,
                )}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='withdrawal-detail-withdrawal-total-amount'
            >
              <Span variant='bold' text='Total Amount' />
              <Span
                variant='thin'
                text={displayDollar(
                  String(
                    (withdrawalRequestDetail &&
                      withdrawalRequestDetail?.metadata?.receivedAmount) ??
                      0,
                  ),
                  (withdrawalRequestDetail &&
                    withdrawalRequestDetail?.platform_fee?.currency_code) ??
                    'USD',
                  2,
                  true,
                )}
              />
            </Flex>
            {withdrawalRequestDetail && withdrawalRequestDetail?.quantity && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='withdrawal-detail-withdrawal-units-sold'
              >
                <Span variant='bold' text='Units Sold' />
                <Span variant='thin' text={Number(withdrawalRequestDetail?.quantity).toString()} />
              </Flex>
            )}
            <Flex variant='space-between' classes={['mt-16']}>
              {displayWithdrawalStatusModal ? (
                <InputModal>
                  <Flex
                    variant='column'
                    classes={['input-modal']}
                    dataTestId='withdrawal-detail-withdrawal-update-status-modal'
                  >
                    <Flex variant='space-between'>
                      <H4 text={'Update Redemption Status'} />
                      <Close
                        height='24px'
                        onClick={() => {
                          setDisplayWithdrawalStatusModal(false)
                          setWithdrawalStatusValue(withdrawalRequestDetail.status)
                        }}
                        dataTestId='withdrawal-detail-withdrawal-update-status-modal-close-button'
                      />
                    </Flex>
                    <Line />
                    <Flex variant='column' classes={['reply-section']}>
                      <Span variant='bold' text='Are you sure? You want to update.' />
                      <SelectDropdown
                        name='withdrawalStatus'
                        id='withdrawalStatusId'
                        formId=''
                        list={getAllowedWithdrawalStatusOptions(withdrawalRequestDetail?.status)}
                        value={withdrawalStatusValue || withdrawalRequestDetail.status}
                        handleChange={(val) => {
                          const defaultSent = getDefaultActualAmountSentStr()

                          if (val === 'REJECTED') {
                            setError('This field cannot be empty')
                          } else if (val === 'TRADE_EXECUTED') {
                            setError(
                              isPositiveDecimalAmount(defaultSent)
                                ? ''
                                : 'Please enter a valid amount',
                            )
                          } else {
                            setError('')
                          }

                          setWithdrawalStatusValue(val)
                          setIsManualUnitsInputEnabled(false)
                          setUnits('')
                          setActualAmountSentStr(val === 'TRADE_EXECUTED' ? defaultSent : '')
                        }}
                        dataTestId='withdrawal-detail-withdrawal-update-status-select'
                      />

                      {withdrawalStatusValue && withdrawalStatusValue === 'REJECTED' && (
                        <>
                          <Input
                            labelText='Reason'
                            type='text'
                            name='reason'
                            placeholder='Enter reason for rejection'
                            value={description}
                            handleChange={(e) => {
                              if (e.target.value) setError('')
                              else setError('This field cannot be empty')
                              setDescription(e.target.value)
                            }}
                            required
                            dataTestId='withdrawal-detail-withdrawal-update-status-rejection-reason-input'
                          />
                          <StatusLabel
                            type='error'
                            variant='basic'
                            text={error}
                            dataTestId='withdrawal-detail-rejected-reason-error'
                          />
                        </>
                      )}

                      {withdrawalStatusValue === 'TRADE_EXECUTED' && (
                        <>
                          <CheckboxInput
                            name='manual-units'
                            labelText='Enter units manually'
                            value={isManualUnitsInputEnabled ? 'true' : 'false'}
                            handleChange={(e) => {
                              const shouldEnableManualUnits = e.target.checked
                              setIsManualUnitsInputEnabled(shouldEnableManualUnits)
                              if (!shouldEnableManualUnits) {
                                setUnits('')
                                setError('')
                              }
                            }}
                            dataTestId='withdrawal-detail-trade-executed-manual-units-checkbox'
                          />

                          {isManualUnitsInputEnabled && (
                            <>
                              <Input
                                labelText='Units'
                                type='number'
                                name='units'
                                placeholder='Enter no of units to be sold'
                                value={units}
                                handleChange={(e) => {
                                  setUnits(e.target.value)
                                  const hasRequiredAmount =
                                    isPositiveDecimalAmount(actualAmountSentStr)
                                  if (e.target.value && hasRequiredAmount) setError('')
                                  else setError('This field cannot be empty')
                                }}
                                required
                                dataTestId='withdrawal-detail-trade-executed-units-input'
                              />
                              <StatusLabel
                                type='error'
                                variant='basic'
                                text={error}
                                dataTestId='withdrawal-detail-trade-executed-error'
                              />
                            </>
                          )}
                          <Input
                            labelText='Actual amount sent'
                            type='text'
                            name='actualAmountSent'
                            placeholder='Amount'
                            value={actualAmountSentStr}
                            handleChange={(e) => {
                              setActualAmountSentStr(e.target.value)
                              const hasRequiredUnits = !isManualUnitsInputEnabled || units.trim()
                              if (isPositiveDecimalAmount(e.target.value) && hasRequiredUnits)
                                setError('')
                              else setError('Please enter a valid amount')
                            }}
                            required
                            dataTestId='withdrawal-detail-actual-amount-sent-input'
                          />
                          {!isManualUnitsInputEnabled && (
                            <>
                              <Span
                                variant='thin'
                                text='Units will be calculated as amount ÷ current unit price (NAV), unless you enter units manually.'
                                classes={['mt-8']}
                                dataTestId='withdrawal-detail-trade-executed-units-hint'
                              />
                              <StatusLabel
                                type='error'
                                variant='basic'
                                text={error}
                                dataTestId='withdrawal-detail-trade-executed-amount-error'
                              />
                            </>
                          )}
                        </>
                      )}

                      {confirmationModal ? (
                        <InputModal>
                          <Flex
                            variant='column'
                            classes={['input-modal']}
                            dataTestId='withdrawal-detail-update-status-confirmation-modal'
                          >
                            <H4 text={'This action cannot be undone!'} />
                            <Line />
                            <Flex variant='column' classes={['reply-section']}>
                              <Span
                                variant='bold'
                                text='Are you sure, you want to update status and send status update email?'
                              />

                              <Button
                                classes={['mt-24']}
                                variant='primary-btn'
                                text='Yes'
                                handleClick={async () => {
                                  setConfirmationModal(false)

                                  const result = await handleStatus({
                                    value: withdrawalStatusValue,
                                    field: 'withdrawalStatus',
                                    description: description.trim(),
                                    ...(isManualUnitsInputEnabled ? { units: Number(units) } : {}),
                                    actualAmountSent: actualAmountSentStr || undefined,
                                  })

                                  if (result.success) {
                                    setDisplayWithdrawalStatusModal(false)
                                  } else if (result.errorMessage || result.errorCode) {
                                    setError(
                                      result.errorMessage ||
                                        getOrderStatusErrorMessage(
                                          { data: { code: result.errorCode } },
                                          'Something went wrong in updating withdrawal status',
                                        ),
                                    )
                                  }
                                }}
                              />

                              <Button
                                classes={['mt-24']}
                                variant='secondary-btn'
                                text='No'
                                handleClick={() => {
                                  setConfirmationModal(false)
                                  setDisplayWithdrawalStatusModal(false)
                                }}
                              />
                            </Flex>
                          </Flex>
                        </InputModal>
                      ) : null}

                      <Button
                        classes={['mt-24']}
                        variant='primary-btn'
                        text='Update'
                        handleClick={() => {
                          if (withdrawalStatusValue === 'REJECTED' && !description.trim()) {
                            setError('This field cannot be empty')
                            return
                          }

                          if (
                            withdrawalStatusValue === 'TRADE_EXECUTED' &&
                            isManualUnitsInputEnabled &&
                            !units
                          ) {
                            setError('This field cannot be empty')
                            return
                          }

                          if (
                            withdrawalStatusValue === 'TRADE_EXECUTED' &&
                            !isPositiveDecimalAmount(actualAmountSentStr)
                          ) {
                            setError('Please enter a valid amount')
                            return
                          }

                          setError('')
                          setConfirmationModal(true)
                        }}
                        dataTestId='withdrawal-detail-update-withdrawal-status-update-button'
                        isDisabled={!!error}
                      />
                    </Flex>
                  </Flex>
                </InputModal>
              ) : null}
              <Button
                variant='primary-btn'
                text='Update Redemption Status'
                isDisabled={
                  withdrawalRequestDetail?.status === 'TRADE_EXECUTED' ||
                  withdrawalRequestDetail?.status === 'REJECTED' ||
                  withdrawalRequestDetail?.status === 'CANCELLED' ||
                  withdrawalRequestDetail?.status === 'INITIATED' ||
                  !allowWithdrawalOrderStatusUpdates
                }
                handleClick={() => {
                  setError('')
                  setActualAmountSentStr('')
                  setDisplayWithdrawalStatusModal(true)
                }}
                dataTestId='withdrawal-detail-update-withdrawal-status-button'
              />
            </Flex>
          </Card>
        )}
      </Flex>

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        <Card classes={['p-40', 'mb-48', 'mr-16']}>
          <P text='Client Important checklist' classes={['mb-16']} />
          <Flex variant='space-between'>
            <Span variant='bold' text='Email Verified' />

            {investorProfile && investorProfile.isEmailVerified ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-email-verified-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-email-unverified-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='KYC Complete' />

            {kycData && kycData.status === KycStatus.APPROVED ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-kyc-complete-icon'
              />
            ) : (
              <ToolTip
                content={kycData && kycData.status}
                contentClassNames={['tool-tip-container-fit']}
              >
                <span>
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='withdrawal-detail-kyc-incomplete-icon'
                  />
                </span>
              </ToolTip>
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Confidentiality Agreement Accepted' />

            {kycData && kycData.is_nda_accepted ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-nda-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-nda-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Risk Checklist Accepted' />

            {kycData && kycData.investment_essentials_agreement ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-risk-checklist-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-risk-checklist-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Terms of Business Accepted' />

            {kycData && kycData.accept_tob ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-terms-of-business-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-terms-of-business-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Transaction Checklist Accepted' />

            {kycData && kycData.transaction_checklist_agreement ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-transaction-checklist-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-transaction-checklist-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Client Agreement Signed' />

            {kycData && kycData.agreement_document_id ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-agreement-signed-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-agreement-not-signed-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Welcome Letter Shared' />

            {kycData && kycData?.metadata?.welcomeLetter ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-welcome-letter-shared-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-welcome-letter-not-shared-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Self Declaration Agreement' />

            {kycData && kycData?.self_declaration ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-self-declaration-agreement-signed-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='withdrawal-detail-self-declaration-agreement-not-signed-icon'
              />
            )}
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}

export { WithdrawalDetailPresenter }
